    <template>
        <form @submit.prevent="saveAvailability">
            <div class="row">
                <div class="col-12 mb-3">
                    <div class="d-flex gap-4 align-items-center">
                        <!-- Advanced Options -->
                        <h6 class="my-0 py-0">Advanced Options</h6>
                        <MaterialSwitch name="advancedOptions" id="advancedOptions" :checked="showAdvancedOption"
                            @change="onAdvancedChange" />
                    </div>

                    <div class="row d-flex align-items-end">
                        <div class="col-md-6 mb-3">
                            <material-input name="repeatEvery" id="repeatEvery" type="number" label="Repeats"
                                v-model="recurrence.repeatEvery" />
                        </div>
                        <div class="col-md-6 mb-3">
                            <MaterialSelect name="repeatEveryType" id="repeatEveryType"
                                :options="repeatEveryTypeOptions" v-model="recurrence.repeatEveryType" />
                        </div>
                    </div>

                    <div class="mb-3">
                        <label class="form-label mb-3">Days:</label>
                        <div class="d-flex flex-wrap gap-3">
                            <MaterialSwitch v-for="day in weekDays" :key="day.value" :id="`day_${day.value}`"
                                :name="`day-${day.value}`" :label="day.label"
                                :checked="recurrence.days.includes(day.value)" @change="() => toggleDay(day.value)" />
                        </div>
                    </div>

                    <div v-if="showAdvancedOption" class="mb-3">
                        <label class="form-label mb-3">Week (of Month):</label>
                        <div class="d-flex flex-wrap gap-3">
                            <MaterialSwitch v-for="week in weekOnMonth" :key="week.value" :id="`week_${week.value}`"
                                :name="`weekOfMonth-${week.value}`" :label="week.label"
                                :checked="recurrence.weekOfMonth.includes(week.value)"
                                @change="() => toggleWeeksOfMonth(week.value)" />
                        </div>
                    </div>
                </div>

                <div v-if="showAdvancedOption" class="col-12">
                    <div class="mb-3">
                        <MaterialMultiSelect name="selectedDays" v-model="recurrence.selectedDays" :items="daysOfMonth"
                            label="On Days of Month" placeholder="Select Dates..." itemTrackBy="label" itemLabel="label"
                            emitAs="object" :showTextField="true" />
                    </div>

                    <div class="mb-3">
                        <label class="form-label mb-3">On Week of Month:</label>
                        <div class="d-flex flex-wrap gap-3">
                            <MaterialSwitch v-for="week in weekOfMonth2" :key="week.value" :id="`weeks_${week.value}`"
                                :name="`weeks-${week.value}`" :label="week.label"
                                :checked="recurrence.weeks.includes(week.value)"
                                @change="() => toggleWeeks(week.value)" />
                        </div>
                    </div>

                    <div class="mb-3">
                        <MaterialMultiSelect name="weeksOfYear" v-model="recurrence.weeksOfYear" :items="weeksOfYear()"
                            label="Week of Year" placeholder="Select Week..." itemTrackBy="label" itemLabel="label"
                            emitAs="object" :showTextField="true" />
                    </div>

                    <div class="mb-3">
                        <MaterialMultiSelect name="months" v-model="recurrence.months" :items="months" label="Month"
                            placeholder="Select Month..." itemTrackBy="label" itemLabel="label" emitAs="object"
                            :showTextField="true" />
                    </div>
                </div>

                <div class="row mt-2">
                    <div class="col-md-6 mb-3">
                        <MaterialDatePicker name="startDate" id="startDate" label="Start Date"
                            v-model="recurrence.startDate" :readOnly="true" />
                    </div>
                    <div class="col-md-6 mb-3">
                        <div>
                            <MaterialDatePicker name="endDaate" id="endDate" label="End Date"
                                v-model="recurrence.endDate" :disabled="recurrence.indefinite" :readOnly="true" />
                        </div>
                        <div class="d-flex justify-content-end my-2">
                            <MaterialSwitch name="indefiniteEndDate" id="indefinite" label="Indefinite End Date"
                                :checked="recurrence.indefinite"
                                @change="(e) => (recurrence.indefinite = e && e.target ? e.target.checked : !!e)" />
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-6 mb-3">
                    <material-input name="recurrenceName" id="name" type="text" label="Recurrence Name"
                        v-model="recurrence.name" />
                </div>
                <div class="col-md-6 mb-3">
                    <material-input name="recurrenceDescription" id="description" type="text" label="Description"
                        v-model="recurrence.description" />
                </div>
            </div>
        </form>
    </template>

<script setup>
import { ref, watch } from "vue";
// Accept details prop for edit mode
const props = defineProps({
    details: {
        type: Object,
        default: null
    },
    activityId: {
        type: [Number, String],
        default: null
    }
});
import MaterialInput from '@/components/common/MaterialInput.vue';
import MaterialSelect from '@/components/common/MaterialSelect.vue';
import MaterialSwitch from '@/components/common/MaterialSwitch.vue';
import MaterialMultiSelect from "@/components/common/MaterialMultiSelect.vue";
import MaterialDatePicker from '@/components/common/MaterialDatePicker.vue';
import store from "../../../store";
import { useRoute } from "vue-router";

// Initial default state factory
const defaultName = props.activityId ? `activity_${props.activityId}_recurrence` : "";
const getInitialState = () => ({
    availabilityid: "",
    recurrenceid: "",
    name: defaultName,
    description: defaultName,
    type: "available",
    dateRange: "range",
    repeatEvery: 1,
    repeatEveryType: "",
    days: [],
    weeks: [],
    weekOfMonth: [],
    weekOfMonth2: [],
    selectedDays: [],
    months: [],
    weeksOfYear: [],
    startDate: "",
    endDate: "",
    indefinite: false,
    applyToEntityId: "",
    applyToEntityType: "",
    _entities: {}
});
const recurrence = ref(getInitialState());

// reactive flag for advanced options
const showAdvancedOption = ref(false);
const route = useRoute();

// separate small note models for each multi-select
const noteSelectedDays = ref("");
const noteWeeksOfYear = ref("");
const noteMonths = ref("");

const emit = defineEmits(["saved", "save-error", "error"]);

const weekDays = [
    { label: "Mon", value: "1" },
    { label: "Tue", value: "2" },
    { label: "Wed", value: "3" },
    { label: "Thu", value: "4" },
    { label: "Fri", value: "5" },
    { label: "Sat", value: "6" },
    { label: "Sun", value: "7" },
];

const weekOnMonth = [
    { label: "First", value: "1" },
    { label: "Second", value: "2" },
    { label: "Third", value: "3" },
    { label: "Fourth", value: "4" },
    { label: "Fifth", value: "5" }
];

const weekOfMonth2 = [
    { label: "Week 1", value: "1" },
    { label: "Week 2", value: "2" },
    { label: "Week 3", value: "3" },
    { label: "Week 4", value: "4" },
    { label: "Week 5", value: "5" },
    { label: "Week 6", value: "6" },
];

const repeatEveryTypeOptions = [
    { label: "Day(s)", value: "day" },
    { label: "Week(s)", value: "week" },
    { label: "Month(s)", value: "month" },
    { label: "Year(s)", value: "year" },
];

// The backend (and the working setup wizard) expect:
//  - recurrenceperiod = numeric repeat count, frequency = unit word ("weeks")
//  - daysofweek as day names ("sunday" .. "saturday")
// The UI weekday switches use Mon=1 .. Sun=7, so map between the two here.
const dayValueToName = { "1": "monday", "2": "tuesday", "3": "wednesday", "4": "thursday", "5": "friday", "6": "saturday", "7": "sunday" };
const dayNameToValue = { monday: "1", tuesday: "2", wednesday: "3", thursday: "4", friday: "5", saturday: "6", sunday: "7" };
const periodTypeToFrequency = { day: "days", week: "weeks", month: "months", year: "years" };
const frequencyToPeriodType = { days: "day", weeks: "week", months: "month", years: "year" };
// Far-future end date used to represent an indefinite recurrence (matches wizard).
const INDEFINITE_END_DATE = "01/01/2099";

const daysOfMonth = Array.from({ length: 31 }, (_, i) => {
    const n = i + 1;
    const suffix =
        n % 100 >= 11 && n % 100 <= 13
            ? "th"
            : n % 10 === 1
                ? "st"
                : n % 10 === 2
                    ? "nd"
                    : n % 10 === 3
                        ? "rd"
                        : "th";
    return { value: n, label: `${n}${suffix}` };
});

const weeksOfYear = ({ weeks = 52, labelPrefix = "Week " } = {}) =>
    Array.from({ length: weeks }, (_, i) => {
        const n = i + 1;
        return {
            label: `${labelPrefix}${n}`,
            value: n,
        };
    });

const months = [
    { label: "January", value: "1" },
    { label: "February", value: "2" },
    { label: "March", value: "3" },
    { label: "April", value: "4" },
    { label: "May", value: "5" },
    { label: "June", value: "6" },
    { label: "July", value: "7" },
    { label: "August", value: "8" },
    { label: "September", value: "9" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" },
];

function toggleDay(day) {
    if (recurrence.value.days.includes(day)) {
        recurrence.value.days = recurrence.value.days.filter((d) => d !== day);
    } else {
        recurrence.value.days.push(day);
    }
}

function toggleWeeks(week) {
    if (recurrence.value.weeks.includes(week)) {
        recurrence.value.weeks = recurrence.value.weeks.filter((d) => d !== week);
    } else {
        recurrence.value.weeks.push(week);
    }
}

function toggleWeeksOfMonth(week) {
    if (recurrence.value.weekOfMonth.includes(week)) {
        recurrence.value.weekOfMonth = recurrence.value.weekOfMonth.filter((d) => d !== week);
    } else {
        recurrence.value.weekOfMonth.push(week);
    }
}

// async function saveAvailability() {
//     const payload = {
//         cmd: props.details ? "editrecurrence" : "addrecurrence",
//         activityid: Number(route.params.activityid),
//         recurrenceperiod: recurrence.value.repeatEveryType || "weekly",
//         frequency: recurrence.value.repeatEvery,
//         daysofweek: recurrence.value.days.map(String),
//         daysofmonth: recurrence.value.selectedDays.map(d => String(d.value)),
//         weeksofmonth: recurrence.value.weeks.map(String),
//         weekdaysofmonth: recurrence.value.weekOfMonth.map(String),
//         weeksofyear: recurrence.value.weeksOfYear.map(d => String(d.value)),
//         months: recurrence.value.months.map(d => String(d.value)),
//         recurstartdate: recurrence.value.startDate,
//         recurenddate: recurrence.value.indefinite ? null : recurrence.value.endDate,
//         recurrencename: recurrence.value.name,
//         recurrencedescription: recurrence.value.description,
//         recurrenceid: recurrence.value.recurrenceid // required for edit
//     };

//     try {
//         let res;
//         if (props.details) {
//             // EDIT
//             res = await store.dispatch("editRecurrence", payload);
//         } else {
//             // ADD
//             res = await store.dispatch("addRecurrence", payload);
//         }

//         const ok = res && (res.returncode === 0 || res.success || res.status === "ok");
//         if (!ok) {
//             throw new Error(res?.message || "Failed to save recurrence");
//         }

//         emit("saved", res);
//     } catch (err) {
//         console.error("Save availability error:", err);
//         emit("save-error", err);
//         throw err;
//     }
// }

async function saveAvailability() {
    // Determine edit/add from the form model itself
    const isEdit = !!(recurrence.value.recurrenceid && String(recurrence.value.recurrenceid).trim().length > 0)

    const payload = {
        cmd: isEdit ? "editrecurrence" : "addrecurrence",
        activityid: Number(route.params.activityid),
        // recurrenceperiod = numeric count, frequency = unit word (backend contract)
        recurrenceperiod: String(recurrence.value.repeatEvery || 1),
        frequency: periodTypeToFrequency[recurrence.value.repeatEveryType] || "weeks",
        // Send weekdays as names so the backend day-of-week validation matches.
        daysofweek: recurrence.value.days.map(d => dayValueToName[String(d)] || String(d)),
        daysofmonth: recurrence.value.selectedDays.map(d => String(d.value)),
        weeksofmonth: recurrence.value.weeks.map(String),
        weekdaysofmonth: recurrence.value.weekOfMonth.map(String),
        weeksofyear: recurrence.value.weeksOfYear.map(d => String(d.value)),
        months: recurrence.value.months.map(d => String(d.value)),
        recurstartdate: recurrence.value.startDate,
        // Indefinite end date -> far-future date (matches the working wizard payload).
        recurenddate: recurrence.value.indefinite ? INDEFINITE_END_DATE : recurrence.value.endDate,
        recurrencename: recurrence.value.name,
        recurrencedescription: recurrence.value.description,
        recurrenceid: recurrence.value.recurrenceid // required for edit
    };

    try {
        let res;
        if (isEdit) {
            // EDIT
            res = await store.dispatch("editRecurrence", payload);
        } else {
            // ADD
            res = await store.dispatch("addRecurrence", payload);
        }

        const ok = res && (res.returncode === 0 || res.success || res.status === "ok");
        if (!ok) {
            throw new Error(res?.message || "Failed to save recurrence");
        }

        emit("saved", res);
    } catch (err) {
        console.error("Save availability error:", err);
        emit("save-error", err);
        throw err;
    }
}

// Reset form to initial state
function resetForm() {
    recurrence.value = getInitialState();
    showAdvancedOption.value = false;
    noteSelectedDays.value = "";
    noteWeeksOfYear.value = "";
    noteMonths.value = "";
}

// updated handler: sets reactive ref from switch event (robust to event or boolean)
function onAdvancedChange(e) {
    showAdvancedOption.value = e && e.target ? e.target.checked : !!e;
}

// Expose methods to parent component
defineExpose({ saveAvailability, resetForm });

// Guarded watch so we don't overwrite form repeatedly and cause recursive updates
watch(
    () => props.details,
    (d) => {
        if (!d) {
            // if parent cleared details, reset form
            recurrence.value = getInitialState();
            showAdvancedOption.value = false;
            return;
        }

        // only update when incoming recurrenceid differs from current form (prevents infinite loop)
        if (recurrence.value.recurrenceid && recurrence.value.recurrenceid === d.recurrenceid) {
            return;
        }

        recurrence.value = {
            ...getInitialState(),
            availabilityid: d.availabilityid || "",
            recurrenceid: d.recurrenceid || "",
            name: d.recurrencename || d.name || "",
            description: d.recurrencedescription || d.description || "",
            type: d.available === false ? "unavailable" : "available",
            dateRange: d.dateRange || "range",
            repeatEvery: d.recurrenceperiod || 1,
            repeatEveryType: frequencyToPeriodType[String(d.frequency).toLowerCase()] || "week",
            days: (d.daysofweek || []).map(v => dayNameToValue[String(v).toLowerCase()] || String(v)),
            weeks: (d.weeksofmonth || []).map(String),
            weekOfMonth: (d.weekdaysofmonth || []).map(String),
            weekOfMonth2: [],
            selectedDays: (d.daysofmonth || []).map(v => ({ value: v, label: String(v) })),
            months: (d.months || []).map(v => ({ value: v, label: months.find(m => m.value == v.toString())?.label || String(v) })),
            weeksOfYear: (d.weeksofyear || []).map(v => ({ value: v, label: `Week ${v}` })),
            startDate: d.recurstartdate || d.startdate || "",
            endDate: d.recurenddate || d.enddate || "",
            indefinite: d.recurenddate === null || d.indefinite || false,
        };

        // set advanced flag if there are advanced fields present
        showAdvancedOption.value =
            (recurrence.value.selectedDays && recurrence.value.selectedDays.length > 0) ||
            (recurrence.value.months && recurrence.value.months.length > 0) ||
            (recurrence.value.weeksOfYear && recurrence.value.weeksOfYear.length > 0);
    },
    { immediate: true }
);
</script>